import type { LegacyMatch } from "../api/legacyTypes";

export interface FeederCandidate {
  name: string;
  teamId: string;
  logo: string;
}

export interface SlotFeederHint {
  label: string;
  candidates: FeederCandidate[];
  sourceKeys: string[];
}

function names(match: LegacyMatch) {
  return [match.participant1, match.participant2, match.participant3, match.participant4].filter(Boolean);
}

function roundIndex(match: LegacyMatch) {
  const parsed = match.sourceKey.match(/r(\d+)[-_]?m(\d+)$/i);
  return parsed ? { round: Number(parsed[1]), match: Number(parsed[2]) } : undefined;
}

function shortRound(round: string, index?: number) {
  const compact = round.replace(/upper bracket/gi, "UB").replace(/lower bracket/gi, "LB").replace(/quarterfinals?/gi, "QF").replace(/semifinals?/gi, "SF").replace(/grand final/gi, "GF").trim();
  return `${compact}${index ? ` ${index}` : ""}`;
}

function candidates(match: LegacyMatch): FeederCandidate[] {
  return ([1, 2] as const).map((slot) => ({
    name: match[`participant${slot}`],
    teamId: slot === 1 ? match.teamId1 : match.teamId2,
    logo: slot === 1 ? match.logo1 : match.logo2,
  })).filter(({ name }) => Boolean(name));
}

function fromSource(source: LegacyMatch, outcome: "Winner" | "Loser" | "Top 2", index?: number): SlotFeederHint {
  const sourceCandidates = candidates(source);
  return {
    label: `${outcome} of ${shortRound(source.round, index)}`,
    candidates: outcome !== "Top 2" && sourceCandidates.length === 2 ? sourceCandidates : [],
    sourceKeys: [source.sourceKey],
  };
}

function matchesInRound(allMatches: LegacyMatch[], bracket: string, round: string) {
  return allMatches.filter((candidate) => candidate.bracket === bracket && candidate.round.toLowerCase() === round.toLowerCase()).sort((left, right) => left.sortOrder - right.sortOrder);
}

function dotaHint(match: LegacyMatch, slot: number, allMatches: LegacyMatch[]): SlotFeederHint | undefined {
  const round = match.round.toLowerCase();
  const byRound = (name: string) => matchesInRound(allMatches, match.bracket, name);
  if (round === "upper bracket final") {
    const filled = names(match);
    const sources = byRound("Upper bracket semifinals");
    const unresolved = sources.filter((source) => !names(source).some((name) => filled.includes(name)));
    if (unresolved.length === 1) return fromSource(unresolved[0], "Winner", sources.indexOf(unresolved[0]) + 1);
  }
  if (round === "lower bracket quarterfinals") {
    const targets = byRound("Lower bracket quarterfinals");
    const sources = byRound("Upper bracket semifinals");
    const targetIndex = targets.findIndex((target) => target.sourceKey === match.sourceKey);
    const source = sources[sources.length - 1 - targetIndex];
    if (source) return fromSource(source, "Loser", sources.indexOf(source) + 1);
  }
  if (round === "lower bracket semifinal") {
    const sources = byRound("Lower bracket quarterfinals");
    const source = sources[Math.min(slot - 1, sources.length - 1)];
    if (source) return fromSource(source, "Winner", sources.indexOf(source) + 1);
  }
  if (round === "lower bracket final") {
    const source = slot === 1 ? byRound("Lower bracket semifinal")[0] : byRound("Upper bracket final")[0];
    if (source) return fromSource(source, slot === 1 ? "Winner" : "Loser");
  }
  if (round === "grand final") {
    const source = slot === 1 ? byRound("Upper bracket final")[0] : byRound("Lower bracket final")[0];
    if (source) return fromSource(source, "Winner");
  }
  return undefined;
}

export function slotFeederHint(match: LegacyMatch, slot: number, wiki: string, allMatches: LegacyMatch[]): SlotFeederHint | undefined {
  if (wiki === "dota2") {
    const semantic = dotaHint(match, slot, allMatches);
    if (semantic) return semantic;
  }
  const target = roundIndex(match);
  if (target && target.round > 1) {
    const previous = allMatches.filter((candidate) => candidate.bracket === match.bracket && roundIndex(candidate)?.round === target.round - 1).sort((left, right) => (roundIndex(left)?.match ?? 0) - (roundIndex(right)?.match ?? 0));
    const targets = allMatches.filter((candidate) => candidate.bracket === match.bracket && roundIndex(candidate)?.round === target.round);
    const sourceCount = targets.length ? Math.max(1, Math.round(previous.length / targets.length)) : 2;
    const advancing = wiki === "trackmania" ? 2 : 1;
    const sourceOffset = Math.floor((slot - 1) / advancing);
    const source = previous[(target.match - 1) * sourceCount + sourceOffset];
    if (source) return fromSource(source, wiki === "trackmania" ? "Top 2" : "Winner", roundIndex(source)?.match);
  }
  if (wiki === "trackmania" && match.bracket.toLowerCase() === "playoffs" && match.round.toLowerCase().includes("quarterfinal")) {
    return { label: "Group qualifier", candidates: [], sourceKeys: [] };
  }
  return undefined;
}
